import { Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import type { VisualMatch } from "@/components/search/ImageSearchUploader";

interface SearchVisualResultsProps {
  matches: VisualMatch[];
  textAlign: string;
  onItemClick: () => void;
  t: (key: string, fallback: string) => string;
}

/** Renders ranked visual-search matches with similarity badges. */
export const SearchVisualResults = ({
  matches,
  textAlign,
  onItemClick,
  t,
}: SearchVisualResultsProps) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Sparkles className="h-4 w-4 text-primary" />
        <span>{t("visualSearchAdvanced", "תוצאות מדורגות לפי דמיון ויזואלי")}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 p-1">
        <AnimatePresence mode="popLayout">
          {matches.map((match, index) => (
            <motion.div
              key={match.productHandle}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2, delay: index * 0.05 }}
            >
              <Link
                to={`/products/${match.productHandle}`}
                onClick={onItemClick}
                className="group block bg-background rounded-lg border border-border/50 overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300"
              >
                <div className="aspect-square bg-secondary/10 overflow-hidden relative">
                  <img
                    src={match.productImage}
                    alt={match.productTitle}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div
                    className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium ${
                      match.similarityScore >= 80
                        ? "bg-green-500/90 text-white"
                        : match.similarityScore >= 60
                          ? "bg-yellow-500/90 text-white"
                          : "bg-orange-500/90 text-white"
                    }`}
                  >
                    {match.similarityScore}% {t("similarPercent", "דומה")}
                  </div>
                </div>
                <div className={`p-2 sm:p-3 ${textAlign}`}>
                  <h4 className="font-light text-xs sm:text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                    {match.productTitle}
                  </h4>
                  <p className="text-primary font-medium text-xs sm:text-sm">
                    ₪{parseFloat(match.productPrice).toLocaleString()}
                  </p>
                  {match.matchReason && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {match.matchReason}
                    </p>
                  )}
                </div>
              </Link>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
